import React, { useState, useEffect } from 'react';

import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Stack,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  Group as GroupIcon,
  DeleteSweep as DeleteSweepIcon
} from '@mui/icons-material';
import { 
  format, 
  addMonths, 
  subMonths, 
  addDays, 
  subDays, 
  getDay 
} from 'date-fns';
import { es } from 'date-fns/locale';
import Swal from 'sweetalert2';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import citaService from '../../services/citaService';
import PageHeader from '../../components/common/PageHeader';
import ErrorBoundary from '../../components/common/ErrorBoundary';
import { useCalendarColors } from '../../hooks/useCalendarColors';
import { useCalendarState } from '../../hooks/useCalendarState';
import CalendarControls from '../../components/citas/CalendarControls';
import MonthView from '../../components/citas/MonthView';
import WeekView from '../../components/citas/WeekView';
import DayView from '../../components/citas/DayView';
import EventDialog from '../../components/citas/EventDialog';
import { isAndroidDevice, isMobileDevice } from '../../utils/isAndroidWebView';
import WeekViewAndroid from '../../components/citas/WeekViewAndroid';

import { 
  getEstadoColor, 
  isDateInCurrentView, 
  esCitaGrupal 
} from '../../utils/calendarHelpers';
import { esFeriadoPeruano, obtenerNombreFeriado } from '../../utils/feriadosPeruanos';
import GroupRecurringCitaDialog from '../../components/citas/GroupRecurringCitaDialog';
import QuickCreateModal from '../../components/citas/QuickCreateModal';

const CitaCalendar = () => {
  const { showNotification } = useNotification();
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [initialView] = useState(isMobile ? 'day' : 'month');

  // Crear colorConfig temporal inicialmente
  const [tempColorConfig, setTempColorConfig] = useState({
    shouldUseSpecialtyColors: false,
    headerColor: '#f8f9fa',
    getEventColor: (cita) => cita.color || '#858796'
  });
  
  // Usar el hook personalizado para manejar todo el estado
  const {
    // Estados principales
    loading,
    currentDate,
    setCurrentDate,
    selectedDate,
    setSelectedDate,
    currentView,
    setCurrentView,
    events,
    
    // Datos de filtros
    especialistas,
    especialidades,
    selectedEspecialista,
    setSelectedEspecialista,
    selectedEspecialidad,
    setSelectedEspecialidad,
    
    // ✅ NUEVOS: Estados de control por rol
    filtersLocked,
    autoFiltersApplied,
    shouldApplyAutoFilters,
    getUserRole,
    
    // Horarios no laborables
    horariosNoLaborables,
    loadingHorarios,
    isHoraBloqueada,
    
    // Estados de diálogos
    eventDialogOpen,
    setEventDialogOpen,
    selectedEvent,
    setSelectedEvent,
    
    // Estados para acciones rápidas
    reprogramandoCita,
    setReprogramandoCita,
    nuevaFecha,
    setNuevaFecha,
    nuevaHoraInicio,
    setNuevaHoraInicio,
    nuevaHoraFin,
    setNuevaHoraFin,
    
    programandoProximaSesion,
    setProgramandoProximaSesion,
    proximaFecha,
    setProximaFecha,
    proximaHoraInicio,
    setProximaHoraInicio,
    proximaHoraFin,
    setProximaHoraFin,
    
    modificandoHorario,
    setModificandoHorario,
    nuevoPatronDia,
    setNuevoPatronDia,
    nuevoPatronHoraInicio,
    setNuevoPatronHoraInicio,
    nuevoPatronHoraFin,
    setNuevoPatronHoraFin,
    citasAfectadas,
    setCitasAfectadas,
    
    imprimiendoHorario,
    setImprimiendoHorario,
    horarioParaImprimir,
    setHorarioParaImprimir,
    
    // Funciones helper
    updateEventInLocalState,
    addEventToLocalState,
    removeEventFromLocalState
  } = useCalendarState({ ...tempColorConfig, initialView });

  // Crear el colorConfig real una vez que tenemos selectedEspecialista
  const colorConfig = useCalendarColors(selectedEspecialista, especialidades);

 
  // Actualizar tempColorConfig cuando cambie el colorConfig real
  useEffect(() => {
    setTempColorConfig(colorConfig);
  }, [colorConfig]);
 
  const [groupRecurringDialogOpen, setGroupRecurringDialogOpen] = useState(false);
  const [availablePacientes, setAvailablePacientes] = useState([]);
  const [programas, setProgramas] = useState([]);
  const [loadingGroupData, setLoadingGroupData] = useState(false);

  // NUEVOS ESTADOS para eliminación de citas posteriores
  const [eliminarPosterioresModalOpen, setEliminarPosterioresModalOpen] = useState(false);
  const [citaParaEliminarPosteriores, setCitaParaEliminarPosteriores] = useState(null);
  const [loadingEliminarPosteriores, setLoadingEliminarPosteriores] = useState(false);
  const [citasPosterioresInfo, setCitasPosterioresInfo] = useState(null);

  // NUEVO: Estados para QuickCreateModal
const [quickCreateModalOpen, setQuickCreateModalOpen] = useState(false);
const [preselectedDate, setPreselectedDate] = useState(null);
const [preselectedHour, setPreselectedHour] = useState(null);

  // ✅ NUEVA FUNCIÓN: Verificar si usuario puede ver modal vs ir directo al HCE
  const shouldShowModalOnEventClick = () => {
    const userRole = getUserRole();
    // Solo Admin y Admisión ven el modal
    return ['Administrador', 'Admisión'].includes(userRole);
  };

  // ✅ NUEVA FUNCIÓN: Obtener URL del HCE del paciente
  const getHCEUrl = (pacienteId) => {
    if (!pacienteId) return null;
    return `${window.location.origin}/hce/${pacienteId}`;
  };

  // Cargar datos para citas grupales
  const loadPacientesForGroup = async () => {
    if (loadingGroupData) return;
    
    setLoadingGroupData(true);
    try {
      const response = await citaService.getPacientesParaCitasGrupales({
        especialista_id: selectedEspecialista?.id,
        especialidad_id: selectedEspecialidad?.id
      });
      
      setAvailablePacientes(response.data?.pacientes || []);
    } catch (error) {
      console.error('Error cargando pacientes:', error);
      showNotification('Error al cargar pacientes disponibles', 'error');
      setAvailablePacientes([]);
    } finally {
      setLoadingGroupData(false);
    }
  };

  // Cargar programas disponibles
  const loadProgramasDisponibles = async () => {
    try {
      const response = await citaService.obtenerProgramasFiltrados({
        especialidad_id: selectedEspecialidad?.id
      });
      setProgramas(response.data || []);
    } catch (error) {
      console.error('Error cargando programas:', error);
      setProgramas([]);
    }
  };

  // Effect para cargar datos cuando se abre el diálogo
  useEffect(() => {
    if (groupRecurringDialogOpen && canCreate()) {
      loadPacientesForGroup();
      loadProgramasDisponibles();
    }
  }, [groupRecurringDialogOpen, selectedEspecialista, selectedEspecialidad]);

  // Verificar permisos - Solo Admin y Admisión pueden crear
  const canCreate = () => {
    const userRole = getUserRole();
    return ['Administrador', 'Admisión'].includes(userRole);
  };

  // FUNCIÓN AUXILIAR: Formatear fecha
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      let dateToFormat = dateString;
      
      if (dateString.includes('T')) {
        dateToFormat = dateString.split('T')[0];
      }
      
      const [year, month, day] = dateToFormat.split('-');
      const date = new Date(year, month - 1, day, 12, 0, 0);
      
      return format(date, 'dd/MM/yyyy', { locale: es });
    } catch (error) {
      return dateString;
    }
  };

  // NUEVA FUNCIÓN: Eliminar cita permanentemente
  const handleEliminarCita = async (citaId) => {
    if (!window.confirm('¿Está seguro de que desea eliminar permanentemente esta cita? Esta acción no se puede deshacer.')) {
      return;
    }
    
    try {
      await citaService.deleteCita(citaId);
      showNotification('Cita eliminada permanentemente', 'success');
      
      // Actualizar estado local removiendo el evento
      removeEventFromLocalState(citaId);
    } catch (error) {
      console.error('Error deleting appointment:', error);
      showNotification('Error al eliminar la cita', 'error');
    }
  };

  // NUEVA FUNCIÓN: Eliminar cita y posteriores
  const handleEliminarCitaYPosteriores = async (citaId) => {
    try {
      const conteoInfo = await citaService.contarCitasPosteriores(citaId);
      
      if (conteoInfo.total === 0) {
        showNotification('No se encontraron citas posteriores para eliminar', 'warning');
        return;
      }
      
      const citaEncontrada = events.find(event => event.id === citaId);
      
      setCitaParaEliminarPosteriores(citaEncontrada || { id: citaId });
      setCitasPosterioresInfo({
        totalCitas: conteoInfo.total,
        paciente: conteoInfo.paciente,
        fechaDesde: conteoInfo.fecha_desde,
        ejemplos: conteoInfo.ejemplos || [],
        esInformacionCompleta: true
      });
      
      setEliminarPosterioresModalOpen(true);
      
    } catch (error) {
      console.error('Error al consultar citas posteriores:', error);
      showNotification('Error al consultar citas posteriores', 'error');
    }
  };

  // NUEVA FUNCIÓN: Confirmar eliminación de posteriores
  const handleConfirmarEliminacionPosteriores = async () => {
    if (!citaParaEliminarPosteriores) return;
    
    setLoadingEliminarPosteriores(true);
    try {
      const result = await citaService.eliminarCitaYPosteriores(citaParaEliminarPosteriores.id);
      
      showNotification(
        `${result.citas_eliminadas || 0} citas eliminadas exitosamente`, 
        'success'
      );
      
      setEliminarPosterioresModalOpen(false);
      setCitaParaEliminarPosteriores(null);
      setCitasPosterioresInfo(null);
      
      // Recargar eventos después de eliminar
      window.location.reload(); // Opción simple, o implementar refetch mejor
      
    } catch (error) {
      console.error('Error eliminando citas posteriores:', error);
      const errorMessage = error.response?.data?.message || 
                           error.message || 
                           'Error al eliminar las citas posteriores';
      showNotification(errorMessage, 'error');
    } finally {
      setLoadingEliminarPosteriores(false);
    }
  };

  // =============== HANDLERS DE NAVEGACIÓN ===============
  
  // Cambiar la vista
  const handleViewChange = (view) => {
    setCurrentView(view);
    if (view === 'day') {
      setSelectedDate(currentDate);
    }
  };
  
  // Navegar período anterior
  const handlePrev = () => {
    if (currentView === 'month') {
      setCurrentDate(subMonths(currentDate, 1));
    } else if (currentView === 'week') {
      setCurrentDate(subDays(currentDate, 7));
    } else { // day
      let prevDay = subDays(selectedDate, 1);
      if (getDay(prevDay) === 0) {
        prevDay = subDays(prevDay, 1);
      }
      setSelectedDate(prevDay);
    }
  };
  
  // Navegar período siguiente  
  const handleNext = () => {
    if (currentView === 'month') {
      setCurrentDate(addMonths(currentDate, 1));
    } else if (currentView === 'week') {
      setCurrentDate(addDays(currentDate, 7));
    } else { // day
      let nextDay = addDays(selectedDate, 1);
      if (getDay(nextDay) === 0) {
        nextDay = addDays(nextDay, 1);
      }
      setSelectedDate(nextDay);
    }
  };
  
  // Navegar a hoy
  const handleToday = () => {
    const today = new Date();
    setCurrentDate(today);
    setSelectedDate(today);
  };
  
  // ✅ MODIFICADO: Cambiar especialista (solo si no están bloqueados los filtros)
  const handleEspecialistaChange = (event) => {
    if (filtersLocked) {
      showNotification('No puede cambiar el filtro de especialista en su rol', 'warning');
      return;
    }
    setSelectedEspecialista(event.target.value);
  };
  
  // ✅ MODIFICADO: Cambiar especialidad (solo si no están bloqueados los filtros)
  const handleEspecialidadChange = (event) => {
    if (filtersLocked) {
      showNotification('No puede cambiar el filtro de especialidad en su rol', 'warning');
      return;
    }
    setSelectedEspecialidad(event.target.value);
  };

  // =============== HANDLERS DE EVENTOS ===============
  
  // ✅ MODIFICADO: HandleEventClick con comportamiento según rol
  const handleEventClick = (event) => {
     
    // Para especialistas e internos: ir directo al HCE
    if (!shouldShowModalOnEventClick()) {
      const pacienteId = event.paciente?.id || event.extendedProps?.pacienteId;
      
      if (pacienteId) {
        const hceUrl = getHCEUrl(pacienteId);
         window.open(hceUrl, '_blank');
        return;
      } else {
        showNotification('No se pudo obtener el ID del paciente para acceder al HCE', 'warning');
        return;
      }
    }
    
    // Para Admin y Admisión: mostrar modal como antes
    if (event.esGrupalAgrupado) {
      const eventoConInfoGrupal = {
        ...event,
        extendedProps: {
          ...event.extendedProps,
          esGrupalAgrupado: true,
          pacientesGrupo: event.pacientesGrupo,
          totalPacientes: event.totalPacientes,
          grupoNombre: event.grupoNombre
        }
      };
      setSelectedEvent(eventoConInfoGrupal);
    } else {
      setSelectedEvent(event);
    }
    setEventDialogOpen(true);
  };
  
 const handleDateClick = (date, hour = null) => {
  if (getDay(date) === 0) {
    showNotification('No se pueden crear citas los domingos', 'warning');
    return;
  }
  
   
  if (canCreate()) {
    if (selectedEspecialista) {
      setPreselectedDate(date);
      setPreselectedHour(hour);
   
      
      setQuickCreateModalOpen(true);
    } else {
      let url = `/citas/nueva?fecha=${format(date, 'yyyy-MM-dd')}`;
      if (hour) url += `&hora=${hour}`;
      navigate(url);
    }
  } else {
    showNotification('No tiene permisos para crear citas', 'warning');
  }
};
  
// NUEVA FUNCIÓN: Manejar éxito de creación de cita
const handleQuickCreateSuccess = (nuevaCita) => {
  // Cerrar modal
  setQuickCreateModalOpen(false);
  setPreselectedDate(null);
  setPreselectedHour(null);
  
  // ✅ Si viene con refresh: true (citas recurrentes), forzar recarga
  if (nuevaCita?.refresh) {
    // Forzar recarga del calendario cambiando la fecha brevemente
    const tempDate = new Date(currentDate);
    setCurrentDate(new Date(tempDate.getTime() + 1));
    setTimeout(() => setCurrentDate(tempDate), 10);
  } else {
    // Para cita individual, agregar al estado local
    addEventToLocalState(nuevaCita);
  }
  
  // Notificar éxito
  showNotification('Cita creada exitosamente', 'success');
};

  // Cerrar diálogo de evento
  const handleEventDialogClose = () => {
    setEventDialogOpen(false);
  };
  
  // Ir a detalle de cita
  const handleViewCita = () => {
    if (selectedEvent) {
      navigate(`/citas/${selectedEvent.id}`);
    }
    setEventDialogOpen(false);
  };
  
  // Ir a editar cita
  const handleEditCita = () => {
    if (selectedEvent) {
      navigate(`/citas/editar/${selectedEvent.id}`);
    }
    setEventDialogOpen(false);
  };

  // =============== HANDLERS DE ACCIONES RÁPIDAS ===============

  // Cambiar estado rápidamente
  const handleEstadoChange = async (citaId, nuevoEstado) => {
    try {
      await citaService.cambiarEstado(citaId, nuevoEstado);
      
      // Actualizar SOLO el estado local - NO recargar
      updateEventInLocalState(citaId, {
        estado: nuevoEstado,
        color: getEstadoColor(nuevoEstado)
      });
      
      // Actualizar evento seleccionado si existe
      if (selectedEvent && selectedEvent.id === citaId) {
        setSelectedEvent(prev => ({ 
          ...prev, 
          estado: nuevoEstado, 
          color: getEstadoColor(nuevoEstado) 
        }));
      }
      
      showNotification(`Estado cambiado a ${nuevoEstado}`, 'success');
      
    } catch (error) {
      console.error('Error cambiando estado:', error);
      showNotification('Error al cambiar estado', 'error');
    }
  };

  // Iniciar reprogramación simplificada
  const handleIniciarReprogramacion = (event) => {
    const fechaActual = format(new Date(event.fecha), 'yyyy-MM-dd');
    setNuevaFecha(fechaActual);
    setNuevaHoraInicio(event.horaInicio);
    setNuevaHoraFin(event.horaFin);
    setReprogramandoCita(true);
  };

  // Confirmar reprogramación
  const handleConfirmarReprogramacion = async () => {
    if (!nuevaFecha || !nuevaHoraInicio || !nuevaHoraFin) {
      showNotification('Todos los campos son requeridos', 'warning');
      return;
    }

    try {
      // 1. Crear nueva cita
      const nuevaCitaData = {
        paciente_id: selectedEvent.extendedProps?.pacienteId || selectedEvent.paciente?.id,
        especialista_id: selectedEvent.extendedProps?.especialistaId,
        especialidad_id: selectedEvent.extendedProps?.especialidadId,
        fecha: nuevaFecha,
        hora_inicio: nuevaHoraInicio,
        hora_fin: nuevaHoraFin,
        estado: 'Programada',
        motivo: selectedEvent.motivo,
        notas: `Reprogramación de cita ${selectedEvent.id} del ${format(new Date(selectedEvent.fecha), 'dd/MM/yyyy')} a las ${selectedEvent.horaInicio}`,
        etapa_id: selectedEvent.extendedProps?.etapaId,
        numero_sesion: selectedEvent.extendedProps?.numeroSesion
      };

      const nuevaCitaResponse = await citaService.createCita(nuevaCitaData);

      // 2. Actualizar cita original a "Reprogramo"
      await citaService.updateCita(selectedEvent.id, { 
        estado: 'Reprogramo',
        notas: `Reprogramado para ${format(new Date(nuevaFecha), 'dd/MM/yyyy')} a las ${nuevaHoraInicio}`
      });

      // 3. Actualizar estado local
      updateEventInLocalState(selectedEvent.id, {
        estado: 'Reprogramo',
        color: getEstadoColor('Reprogramo'),
        notas: `Reprogramado para ${format(new Date(nuevaFecha), 'dd/MM/yyyy')} a las ${nuevaHoraInicio}`
      });

      // 4. Agregar nueva cita SI está en vista actual
      const fechaNueva = new Date(nuevaFecha);
      const fechaActual = currentView === 'day' ? selectedDate : currentDate;
      
      if (isDateInCurrentView(fechaNueva, fechaActual, currentView)) {
        const nuevoEvento = {
          id: nuevaCitaResponse.data.id,
          title: selectedEvent.pacienteNombre,
          fecha: nuevaFecha,
          horaInicio: nuevaHoraInicio,
          horaInicioNum: parseInt(nuevaHoraInicio.split(':')[0], 10),
          horaFin: nuevaHoraFin,
          color: getEstadoColor('Programada'),
          pacienteNombre: selectedEvent.pacienteNombre,
          especialistaNombre: selectedEvent.especialistaNombre,
          especialidadNombre: selectedEvent.especialidadNombre,
          estado: 'Programada',
          tipo: selectedEvent.tipo,
          motivo: selectedEvent.motivo,
          es_evento_personal: selectedEvent.es_evento_personal || false,
          paciente: selectedEvent.paciente,
          especialidad: selectedEvent.especialidad,
          etapaNombre: selectedEvent.etapaNombre,
          numeroSesion: selectedEvent.numeroSesion,
          extendedProps: selectedEvent.extendedProps
        };
        
        addEventToLocalState(nuevoEvento);
      }

      showNotification('Cita reprogramada exitosamente', 'success');
      
      // Limpiar estados y cerrar
      setReprogramandoCita(false);
      setNuevaFecha('');
      setNuevaHoraInicio('');
      setNuevaHoraFin('');
      setEventDialogOpen(false);
      
    } catch (error) {
      console.error('Error al reprogramar cita:', error);
      showNotification('Error al reprogramar la cita', 'error');
    }
  };

  // Cancelar reprogramación
  const handleCancelarReprogramacion = () => {
    setReprogramandoCita(false);
    setNuevaFecha('');
    setNuevaHoraInicio('');
    setNuevaHoraFin('');
  };

  // Iniciar programación de próxima sesión
  const handleIniciarProximaSesion = (event) => {
    // Sugerir fecha una semana después
    const fechaSugerida = new Date(event.fecha);
    fechaSugerida.setDate(fechaSugerida.getDate() + 7);
    
    setProximaFecha(format(fechaSugerida, 'yyyy-MM-dd'));
    setProximaHoraInicio(event.horaInicio);
    setProximaHoraFin(event.horaFin);
    setProgramandoProximaSesion(true);
  };

  // Confirmar próxima sesión
  const handleConfirmarProximaSesion = async () => {
    if (!proximaFecha || !proximaHoraInicio || !proximaHoraFin) {
      showNotification('Todos los campos son requeridos', 'warning');
      return;
    }

    try {
      const nuevaCitaData = {
        paciente_id: selectedEvent.extendedProps?.pacienteId || selectedEvent.paciente?.id,
        especialista_id: selectedEvent.extendedProps?.especialistaId,
        especialidad_id: selectedEvent.extendedProps?.especialidadId,
        fecha: proximaFecha,
        hora_inicio: proximaHoraInicio,
        hora_fin: proximaHoraFin,
        estado: 'Programada',
        tipo: selectedEvent.tipo,
        motivo: selectedEvent.motivo,
        notas: `Próxima sesión programada desde cita ${selectedEvent.id} del ${format(new Date(selectedEvent.fecha), 'dd/MM/yyyy')}`,
        etapa_id: selectedEvent.extendedProps?.etapaId,
        numero_sesion: selectedEvent.extendedProps?.numeroSesion ? selectedEvent.extendedProps.numeroSesion + 1 : null
      };

      const nuevaCitaResponse = await citaService.createCita(nuevaCitaData);

      // Agregar SOLO SI está en vista actual
      const fechaNueva = new Date(proximaFecha);
      const fechaActual = currentView === 'day' ? selectedDate : currentDate;
      
      if (isDateInCurrentView(fechaNueva, fechaActual, currentView)) {
        const nuevoEvento = {
          id: nuevaCitaResponse.data.id,
          title: selectedEvent.pacienteNombre,
          fecha: proximaFecha,
          horaInicio: proximaHoraInicio,
          horaInicioNum: parseInt(proximaHoraInicio.split(':')[0], 10),
          horaFin: proximaHoraFin,
          color: getEstadoColor('Programada'),
          pacienteNombre: selectedEvent.pacienteNombre,
          especialistaNombre: selectedEvent.especialistaNombre,
          especialidadNombre: selectedEvent.especialidadNombre,
          estado: 'Programada',
          tipo: selectedEvent.tipo,
          motivo: selectedEvent.motivo,
          es_evento_personal: selectedEvent.es_evento_personal || false,
          paciente: selectedEvent.paciente,
          especialidad: selectedEvent.especialidad,
          etapaNombre: selectedEvent.etapaNombre,
          numeroSesion: selectedEvent.extendedProps?.numeroSesion ? selectedEvent.extendedProps.numeroSesion + 1 : null,
          extendedProps: {
            ...selectedEvent.extendedProps,
            numeroSesion: selectedEvent.extendedProps?.numeroSesion ? selectedEvent.extendedProps.numeroSesion + 1 : null
          }
        };
        
        addEventToLocalState(nuevoEvento);
      }

      showNotification('Próxima sesión programada exitosamente', 'success');
      
      // Limpiar y cerrar
      setProgramandoProximaSesion(false);
      setProximaFecha('');
      setProximaHoraInicio('');
      setProximaHoraFin('');
      setEventDialogOpen(false);
      
    } catch (error) {
      console.error('Error al programar próxima sesión:', error);
      showNotification('Error al programar la próxima sesión', 'error');
    }
  };

  // Cancelar próxima sesión
  const handleCancelarProximaSesion = () => {
    setProgramandoProximaSesion(false);
    setProximaFecha('');
    setProximaHoraInicio('');
    setProximaHoraFin('');
  };

  // Iniciar modificación de horario recurrente
  const handleIniciarModificarHorario = async (event) => {
    try {
      // Buscar patrón de citas del mismo paciente y especialista
      const response = await citaService.getCitas({
        paciente_id: event.extendedProps?.pacienteId,
        especialista_id: event.extendedProps?.especialistaId,
        fecha_inicio: format(new Date(), 'yyyy-MM-dd'),
        limite: 100
      });
      
      const citasFuturas = response.data.citas.filter(cita => 
        cita.estado !== 'Realizada' && 
        cita.estado !== 'Cancelada' &&
        new Date(cita.fecha) >= new Date()
      );
      
      setCitasAfectadas(citasFuturas);
      
      // Obtener día actual de la cita seleccionada
      const fechaCita = new Date(event.fecha);
      const diaSemana = fechaCita.getDay();
      const diasSemana = ['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado'];
      
      setNuevoPatronDia(diasSemana[diaSemana]);
      setNuevoPatronHoraInicio(event.horaInicio);
      setNuevoPatronHoraFin(event.horaFin);
      setModificandoHorario(true);
      
    } catch (error) {
      console.error('Error al buscar patrón de citas:', error);
      showNotification('Error al cargar las citas del patrón', 'error');
    }
  };

  // Confirmar modificación de horario
  const handleConfirmarModificarHorario = async () => {
    if (!nuevoPatronDia || !nuevoPatronHoraInicio || !nuevoPatronHoraFin) {
      showNotification('Todos los campos son requeridos', 'warning');
      return;
    }

    const diasSemana = ['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado'];
    const nuevoDiaNumero = diasSemana.indexOf(nuevoPatronDia);
    
    // Filtrar citas que coinciden con el patrón actual
    const fechaSeleccionada = new Date(selectedEvent.fecha);
    const citasDelPatron = citasAfectadas.filter(cita => {
      const fechaCita = new Date(cita.fecha);
      
      const coincidePatron = fechaCita.getDay() === fechaSeleccionada.getDay() &&
                             cita.hora_inicio === selectedEvent.horaInicio;
      
      const esPosteriorOActual = fechaCita >= fechaSeleccionada;
      
      return coincidePatron && esPosteriorOActual;
    });
    
    if (citasDelPatron.length === 0) {
      showNotification('No se encontraron citas futuras con este patrón', 'warning');
      return;
    }

    // Confirmar antes de proceder
    const confirmar = window.confirm(
      `¿Está seguro de modificar ${citasDelPatron.length} citas futuras?\n\n` +
      `Cambiarán de: ${(() => {
        const dias = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
        const diaActual = fechaSeleccionada.getDay();
        return `${dias[diaActual]} ${selectedEvent.horaInicio}-${selectedEvent.horaFin}`;
      })()} \n` +
      `A: ${nuevoPatronDia.charAt(0).toUpperCase() + nuevoPatronDia.slice(1)} ${nuevoPatronHoraInicio}-${nuevoPatronHoraFin}`
    );

    if (!confirmar) return;

    try {
      const updates = citasDelPatron.map(async (cita) => {
        const fechaOriginal = new Date(cita.fecha);
        
        // Calcular nueva fecha basada en el nuevo día de la semana
        const diasDiferencia = nuevoDiaNumero - fechaOriginal.getDay();
        const nuevaFecha = new Date(fechaOriginal);
        nuevaFecha.setDate(fechaOriginal.getDate() + diasDiferencia);
        
        // Si la nueva fecha es en el pasado, mover a la siguiente semana
        if (nuevaFecha < new Date()) {
          nuevaFecha.setDate(nuevaFecha.getDate() + 7);
        }
        
        return citaService.updateCita(cita.id, {
          fecha: format(nuevaFecha, 'yyyy-MM-dd'),
          hora_inicio: nuevoPatronHoraInicio,
          hora_fin: nuevoPatronHoraFin,
          notas: (cita.notas || '') + ` | Horario modificado el ${format(new Date(), 'dd/MM/yyyy')}: desde ${format(fechaSeleccionada, 'dd/MM/yyyy')}`
        });
      });
      
      await Promise.all(updates);
      
      showNotification(`${citasDelPatron.length} citas futuras del patrón actualizadas exitosamente`, 'success');
      
      // Limpiar y cerrar
      setModificandoHorario(false);
      setCitasAfectadas([]);
      setEventDialogOpen(false);
    
    } catch (error) {
      console.error('Error al modificar horario:', error);
      showNotification('Error al modificar el horario recurrente', 'error');
    }
  };

  // Cancelar modificación de horario
  const handleCancelarModificarHorario = () => {
    setModificandoHorario(false);
    setCitasAfectadas([]);
    setNuevoPatronDia('');
    setNuevoPatronHoraInicio('');
    setNuevoPatronHoraFin('');
  };

  // Iniciar impresión de horario
  const handleIniciarImprimirHorario = async (event) => {
    try {
      // Buscar todas las citas futuras del mismo paciente y especialista
      const response = await citaService.getCitas({
        paciente_id: event.extendedProps?.pacienteId,
        especialista_id: event.extendedProps?.especialistaId,
        fecha_inicio: event.fecha,
        limite: 100
      });
      
      const citasFuturas = response.data.citas.filter(cita => 
        cita.estado !== 'Cancelada' &&
        new Date(cita.fecha) >= new Date(event.fecha)
      ).sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
      
      setHorarioParaImprimir(citasFuturas);
      setImprimiendoHorario(true);
      
    } catch (error) {
      console.error('Error al cargar horario:', error);
      showNotification('Error al cargar el horario para imprimir', 'error');
    }
  };

  // Generar e imprimir horario para tiketera
   const handleGenerarImpresion = () => {
    if (horarioParaImprimir.length === 0) {
      showNotification('No hay citas para imprimir', 'warning');
      return;
    }

    // Crear ventana de impresión
    const ventanaImpresion = window.open('', '_blank');
    
    // Obtener información del paciente y especialista
    const primeraCita = horarioParaImprimir[0];
    const pacienteNombre = primeraCita.paciente ? 
      `${primeraCita.paciente.nombres} ${primeraCita.paciente.apellidos}` : 
      'Sin paciente';
    const especialistaNombre = primeraCita.especialista?.usuario ? 
      `${primeraCita.especialista.usuario.nombre} ${primeraCita.especialista.usuario.apellidos}` : 
      'Sin especialista';
    const especialidadNombre = primeraCita.especialidad?.nombre || 'Sin especialidad';

    // Generar HTML para tiketera (72.1mm útiles)
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
          <meta charset="UTF-8">
          <title>Horario - ${pacienteNombre}</title>
          <style>
              @page { 
                  margin: 2mm; 
                  size: 80mm 297mm;
              }
              body {
                  font-family: Arial, Helvetica, sans-serif;
                  margin: 0; 
                  padding: 3mm; 
                  background: white; 
                  color: black;
                  font-size: 12px;
                  line-height: 1.3;
                  width: 70mm;
                  max-width: 70mm;
              }
              .header { 
                  text-align: center; 
                  margin-bottom: 6mm; 
                  border-bottom: 1px solid black;
                  padding-bottom: 6mm; 
              }
              .logo { 
                  max-width: 55mm;
                  max-height: 20mm;
                  margin: 0 auto 2mm;
                  display: block;
              }
              .clinic-name { 
                  font-size: 12px; 
                  font-weight: bold; 
                  margin-bottom: 2mm; 
              }
              .title { 
                  font-size: 14px;
                  font-weight: bold; 
                  text-align: center; 
                  margin: 4mm 0; 
                  text-transform: uppercase; 
              }
              .patient-info { 
                  margin-bottom: 4mm; 
                  font-size: 12px;
                  border-bottom: 1px solid black;
                  padding-bottom: 4mm; 
              }
              .info-line { 
                  margin-bottom: 2mm; 
                  word-wrap: break-word; 
              }
              .label { 
                  font-weight: bold; 
              }
              .schedule-table { 
                  width: 100%; 
                  border-collapse: collapse; 
                  margin: 3mm 0 5mm; 
                  font-size: 11px;
                  border: 1px solid black;
              }
              .schedule-table th { 
                  padding: 2mm 1mm;
                  text-align: center; 
                  font-weight: bold; 
                  font-size: 11px;
                  border: 1px solid black;
                  background: white;
                  color: black;
              }
              .schedule-table td { 
                  padding: 2mm 1mm; 
                  text-align: center; 
                  font-size: 11px;
                  border: 1px solid black;
              }
              .status { 
                  font-weight: bold; 
                  font-size: 11px;
              }
              .summary { 
                  text-align: center; 
                  margin: 3mm 0; 
                  font-weight: bold; 
                  font-size: 12px; 
                  border: 1px solid black;
                  padding: 3mm; 
              }
              .footer { 
                  margin-top: 4mm; 
                  padding-top: 3mm; 
                  border-top: 1px solid black;
                  text-align: center; 
                  font-size: 10px; 
              }
              @media print {
                  body { 
                      -webkit-print-color-adjust: exact; 
                      print-color-adjust: exact; 
                  }
              }
          </style>
      </head>
      <body>
          <div class="header">
              <img src="${window.location.origin}/assets/logo.png" alt="Logo" class="logo" />
          </div>
          <div class="title">HORARIO DE CITAS</div>
          <div class="patient-info">
              <div class="info-line"><span class="label">Paciente:</span> ${pacienteNombre}</div>
              <div class="info-line"><span class="label">Especialista:</span> ${especialistaNombre}</div>
              <div class="info-line"><span class="label">Especialidad:</span> ${especialidadNombre}</div>
          </div>
          <div class="summary">Total: ${horarioParaImprimir.length} citas</div>
          <table class="schedule-table">
              <thead><tr>
                  <th>Fecha</th>
                  <th>Día</th>
                  <th>Hora</th>
                  <th>Estado</th>
              </tr></thead>
              <tbody>
                  ${horarioParaImprimir.map(cita => {
                    const fecha = new Date(cita.fecha);
                    const dias = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
                    
                    return `
                      <tr>
                          <td>${format(fecha, 'dd/MM')}</td>
                          <td>${dias[fecha.getDay()]}</td>
                          <td>${cita.hora_inicio.substring(0,5)}</td>
                          <td>${cita.estado}</td>
                      </tr>
                    `;
                  }).join('')}
              </tbody>
          </table>
          <div class="footer">
              <div>Impreso: ${format(new Date(), 'dd/MM/yyyy HH:mm')}</div>
              <div>Por favor llegue 5 minutos antes de su cita</div>
          </div>
          <script>
            window.onload = function() {
              setTimeout(() => {
                window.print();
                setTimeout(() => window.close(), 500);
              }, 800);
            };
          </script>
      </body>
      </html>
    `;

    // Escribir contenido y activar impresión
    ventanaImpresion.document.write(htmlContent);
    ventanaImpresion.document.close();
    
    // Cerrar modal
    setImprimiendoHorario(false);
    setHorarioParaImprimir([]);
    setEventDialogOpen(false);
  };

  // Cancelar impresión
  const handleCancelarImpresion = () => {
    setImprimiendoHorario(false);
    setHorarioParaImprimir([]);
  };

  // Handler para citas grupales recurrentes
  const handleGroupRecurringConfirm = async (formData) => {
    try {
      const response = await citaService.crearCitasGrupalesRecurrentes(formData);
      
      showNotification(
        `${response.data.totalCitas} citas grupales creadas exitosamente`,
        'success'
      );
      
      setGroupRecurringDialogOpen(false);
      
      // Recargar events si las citas están en la vista actual
      // Aquí podrías agregar lógica para agregar las citas al estado local
      // o simplemente recargar toda la vista
      window.location.reload(); // Opción simple, o implementar updateEventInLocalState múltiple
      
    } catch (error) {
      console.error('Error creando citas grupales:', error);
      showNotification(
        error.response?.data?.message || 'Error al crear citas grupales recurrentes',
        'error'
      );
    }
  };
  
  const isAndroid = isAndroidDevice();
  // =============== RENDER ===============

  return (
    <Box sx={{ flexGrow: 1 }}>
      <PageHeader 
        title="Calendario de Citas"
        breadcrumbs={[
          { text: 'Dashboard', to: '/dashboard' },
          { text: 'Citas', to: '/citas' },
          { text: 'Calendario' }
        ]}
        actionButton={
          canCreate() && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                component={RouterLink}
                to="/citas/nueva"
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                size={isMobile ? "small" : "medium"}
              >
                {isMobile ? "Nueva" : "Nueva Cita"}
              </Button>
              
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<GroupIcon />}
                onClick={() => setGroupRecurringDialogOpen(true)}
                size={isMobile ? "small" : "medium"}
                disabled={!selectedEspecialista}
                title={!selectedEspecialista ? "Seleccione un especialista primero" : "Crear citas grupales recurrentes"}
              >
                {isMobile ? "Grupal" : "Cita Grupal"}
              </Button>
            </Box>
          )
        }
      />
      
      {/* Controles del calendario */}
      <CalendarControls
        currentDate={currentDate}
        selectedDate={selectedDate}
        currentView={currentView}
        especialistas={especialistas}
        especialidades={especialidades}
        selectedEspecialista={selectedEspecialista}
        selectedEspecialidad={selectedEspecialidad}
        loadingHorarios={loadingHorarios}
        filtersLocked={filtersLocked}
        getUserRole={getUserRole}
        onPrev={handlePrev}
        onNext={handleNext}
        onToday={handleToday}
        onViewChange={handleViewChange}
        onEspecialistaChange={handleEspecialistaChange}
        onEspecialidadChange={handleEspecialidadChange}
      />
      
      {/* Calendario */}
      <Card>
        <CardContent sx={{ p: { xs: 1, md: 2 } }}>
          {loading ? (
            <Box display="flex" justifyContent="center" my={5}>
              <CircularProgress />
            </Box>
          ) : (
            <ErrorBoundary>
              {currentView === 'month' && (
                <MonthView
                  currentDate={currentDate}
                  events={events}
                  onDateClick={handleDateClick}
                  onEventClick={handleEventClick}
                  colorConfig={colorConfig}
                />
              )}
              {currentView === 'week' && (
                isAndroidDevice() ? (
                  <WeekViewAndroid
                    currentDate={currentDate}
                    events={events}
                    onDateClick={handleDateClick}
                    onEventClick={handleEventClick}
                    colorConfig={colorConfig}
                    selectedEspecialista={selectedEspecialista}
                    isHoraBloqueada={isHoraBloqueada}
                    getUserRole={getUserRole}
                  />
                ) : (
                  <WeekView
                    currentDate={currentDate}
                    events={events}
                    onDateClick={handleDateClick}
                    onEventClick={handleEventClick}
                    colorConfig={colorConfig}
                    selectedEspecialista={selectedEspecialista}
                    selectedEspecialidad={selectedEspecialidad}
                    isHoraBloqueada={isHoraBloqueada}
                    horariosNoLaborables={horariosNoLaborables}
                    showNotification={showNotification}
                    refreshEvents={() => window.location.reload()}
                    getUserRole={getUserRole}
                  />
                )
              )}
              {currentView === 'day' && (
                <DayView 
                  selectedDate={selectedDate}
                  events={events}
                  onEventClick={handleEventClick}
                  onDateClick={handleDateClick}
                  colorConfig={colorConfig}
                  selectedEspecialista={selectedEspecialista}
                  selectedEspecialidad={selectedEspecialidad}
                  isHoraBloqueada={isHoraBloqueada}
                  horariosNoLaborables={horariosNoLaborables}
                  getUserRole={getUserRole}
                  showNotification={showNotification}
                  refreshEvents={() => window.location.reload()}
                    onPrev={handlePrev}
                    onNext={handleNext}
                />
              )}
            </ErrorBoundary>
          )}
        </CardContent>
      </Card>
      
      {/* ✅ MODIFICADO: Solo mostrar modal para Admin y Admisión */}
      {shouldShowModalOnEventClick() && (
        <EventDialog
          open={eventDialogOpen}
          onClose={handleEventDialogClose}
          selectedEvent={selectedEvent}
          onViewCita={handleViewCita}
          onEditCita={handleEditCita}
          getUserRole={getUserRole}
          
          // NUEVAS PROPS para eliminación
          onEliminarCita={handleEliminarCita}
          onEliminarCitaYPosteriores={handleEliminarCitaYPosteriores}
          
          // Props para reprogramación
          reprogramandoCita={reprogramandoCita}
          nuevaFecha={nuevaFecha}
          setNuevaFecha={setNuevaFecha}
          nuevaHoraInicio={nuevaHoraInicio}
          setNuevaHoraInicio={setNuevaHoraInicio}
          nuevaHoraFin={nuevaHoraFin}
          setNuevaHoraFin={setNuevaHoraFin}
          onIniciarReprogramacion={handleIniciarReprogramacion}
          onConfirmarReprogramacion={handleConfirmarReprogramacion}
          onCancelarReprogramacion={handleCancelarReprogramacion}
          
          // Props para próxima sesión
          programandoProximaSesion={programandoProximaSesion}
          proximaFecha={proximaFecha}
          setProximaFecha={setProximaFecha}
          proximaHoraInicio={proximaHoraInicio}
          setProximaHoraInicio={setProximaHoraInicio}
          proximaHoraFin={proximaHoraFin}
          setProximaHoraFin={setProximaHoraFin}
          onIniciarProximaSesion={handleIniciarProximaSesion}
          onConfirmarProximaSesion={handleConfirmarProximaSesion}
          onCancelarProximaSesion={handleCancelarProximaSesion}
          
          // Props para modificar horario
          modificandoHorario={modificandoHorario}
          nuevoPatronDia={nuevoPatronDia}
          setNuevoPatronDia={setNuevoPatronDia}
          nuevoPatronHoraInicio={nuevoPatronHoraInicio}
          setNuevoPatronHoraInicio={setNuevoPatronHoraInicio}
          nuevoPatronHoraFin={nuevoPatronHoraFin}
          setNuevoPatronHoraFin={setNuevoPatronHoraFin}
          citasAfectadas={citasAfectadas}
          onIniciarModificarHorario={handleIniciarModificarHorario}
          onConfirmarModificarHorario={handleConfirmarModificarHorario}
          onCancelarModificarHorario={handleCancelarModificarHorario}
          
          // Props para imprimir horario
          imprimiendoHorario={imprimiendoHorario}
          horarioParaImprimir={horarioParaImprimir}
          onIniciarImprimirHorario={handleIniciarImprimirHorario}
          onGenerarImpresion={handleGenerarImpresion}
          onCancelarImpresion={handleCancelarImpresion}
          
          // Otras funciones
          onEstadoChange={handleEstadoChange}
          showNotification={showNotification}
        />
      )}
      
      <GroupRecurringCitaDialog
        open={groupRecurringDialogOpen}
        onClose={() => setGroupRecurringDialogOpen(false)}
        onConfirm={handleGroupRecurringConfirm}
        especialistas={especialistas}
        especialidades={especialidades}
        programas={programas || []}
        pacientes={availablePacientes}
        loading={loading}
      />

      {/* NUEVO MODAL: Eliminación de Citas Posteriores */}
      <Dialog
        open={eliminarPosterioresModalOpen}
        onClose={() => {
          setEliminarPosterioresModalOpen(false);
          setCitaParaEliminarPosteriores(null);
          setCitasPosterioresInfo(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Eliminar Cita y Todas las Posteriores
        </DialogTitle>
        
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>¡Atención!</strong> Esta acción eliminará permanentemente la cita seleccionada 
            y todas las citas posteriores del mismo paciente con el mismo especialista.
          </Alert>
          
          {citaParaEliminarPosteriores && citasPosterioresInfo && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Detalles de la eliminación:
              </Typography>
              
              <Stack spacing={1} sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Paciente:</strong> {citasPosterioresInfo.paciente || 
                    (citaParaEliminarPosteriores.pacienteNombre || 'Sin información')}
                </Typography>
                
                <Typography variant="body2">
                  <strong>Desde la fecha:</strong> {formatDate(
                    citasPosterioresInfo.fechaDesde || citaParaEliminarPosteriores.fecha
                  )}
                </Typography>
                
                <Typography variant="body2" color="error" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                  <strong>Total de citas a eliminar: {citasPosterioresInfo.totalCitas}</strong>
                </Typography>
                
                {/* Mostrar ejemplos si los hay */}
                {citasPosterioresInfo.ejemplos && citasPosterioresInfo.ejemplos.length > 0 && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    <Typography variant="body2" gutterBottom>
                      <strong>Ejemplos de citas que serán eliminadas:</strong>
                    </Typography>
                    {citasPosterioresInfo.ejemplos.map((cita, index) => (
                      <Typography key={index} variant="body2" sx={{ ml: 2 }}>
                        • {formatDate(cita.fecha)} - {cita.hora} ({cita.estado})
                      </Typography>
                    ))}
                    {citasPosterioresInfo.totalCitas > citasPosterioresInfo.ejemplos.length && (
                      <Typography variant="body2" sx={{ ml: 2, fontStyle: 'italic', color: 'primary.main' }}>
                        ... y {citasPosterioresInfo.totalCitas - citasPosterioresInfo.ejemplos.length} citas más
                      </Typography>
                    )}
                  </Alert>
                )}
              </Stack>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button 
            onClick={() => {
              setEliminarPosterioresModalOpen(false);
              setCitaParaEliminarPosteriores(null);
              setCitasPosterioresInfo(null);
            }}
            disabled={loadingEliminarPosteriores}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmarEliminacionPosteriores}
            variant="contained"
            color="error"
            disabled={loadingEliminarPosteriores || !citasPosterioresInfo?.totalCitas}
            startIcon={loadingEliminarPosteriores ? <CircularProgress size={20} /> : <DeleteSweepIcon />}
          >
            {loadingEliminarPosteriores ? 'Eliminando...' : `Eliminar ${citasPosterioresInfo?.totalCitas || 0} Citas`}
          </Button>
        </DialogActions>
      </Dialog>
      {/* NUEVO: Modal de creación rápida */}
      <QuickCreateModal
        open={quickCreateModalOpen}
        onClose={() => {
          setQuickCreateModalOpen(false);
          setPreselectedDate(null);
          setPreselectedHour(null);
        }}
        onSuccess={handleQuickCreateSuccess}
        selectedEspecialista={
          selectedEspecialista && especialistas.length > 0 
            ? especialistas.find(e => e.id === parseInt(selectedEspecialista, 10)) 
            : null
        }
        selectedEspecialidad={selectedEspecialidad}
        preselectedDate={preselectedDate}
        preselectedHour={preselectedHour}
        showNotification={showNotification}
      />
    </Box>
  );
};

export default CitaCalendar;